import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { WikiTreeNode } from '@sd/shared';
import { api, type WikiPageDto } from '../lib/api.js';
import { DvIcon } from '../components/DvIcon.js';
import { getSchema } from '../lib/entitySchemas.js';
import { CATEGORY_TREE, type CatNode } from '../lib/categories.js';

function findCat(nodes: CatNode[], path: string): CatNode | null {
  for (const n of nodes) { if (n.path === path) return n; const f = n.children ? findCat(n.children, path) : null; if (f) return f; }
  return null;
}
function findNode(roots: WikiTreeNode[], path: string): WikiTreeNode | null {
  for (const r of roots) { if (r.path === path) return r; const f = findNode(r.children, path); if (f) return f; }
  return null;
}
function gatherSlugs(node: WikiTreeNode): string[] {
  const out = node.items.map((i) => i.slug);
  for (const c of node.children) out.push(...gatherSlugs(c));
  return out;
}
const sv = (o: Record<string, unknown>, k: string): string => { const v = o[k]; return typeof v === 'string' ? v : typeof v === 'number' ? String(v) : ''; };

interface Facets { type: string; tier: string; status: string }
function facetsOf(p: WikiPageDto): Facets {
  const fm = p.frontmatter as Record<string, unknown>;
  let tier = sv(fm, 'tier'); let status = sv(fm, 'status');
  const blocks = Array.isArray(fm.blocks) ? (fm.blocks as Array<{ type: string; data?: Record<string, unknown> }>) : [];
  for (const b of blocks) {
    const d = b.data ?? {};
    if (b.type === 'adversary-meta' && sv(d, 'tier')) tier = sv(d, 'tier');
    if (b.type === 'stat') { const l = sv(d, 'label').toLowerCase(); if ((l.includes('рівень') || l.includes('tier')) && !tier) tier = sv(d, 'value'); if (l.includes('статус') && !status) status = sv(d, 'value'); }
  }
  return { type: p.type, tier, status };
}

export function WikiGroupOverview(): JSX.Element {
  const params = useParams();
  const groupPath = params['*'] || '_root';
  const [tree, setTree] = useState<WikiTreeNode[] | null>(null);
  const [pages, setPages] = useState<WikiPageDto[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [fType, setFType] = useState<Set<string>>(new Set());
  const [fTier, setFTier] = useState<Set<string>>(new Set());

  useEffect(() => {
    setPages(null); setErr(null); setQ(''); setFType(new Set()); setFTier(new Set());
    api.wikiTree().then((r) => {
      setTree(r.tree);
      const node = findNode(r.tree, groupPath);
      const slugs = node ? gatherSlugs(node) : [];
      if (!slugs.length) { setPages([]); return; }
      return Promise.all(slugs.map((s) => api.wikiPage(s).catch(() => null)))
        .then((ps) => setPages(ps.filter((p): p is WikiPageDto => !!p)));
    }).catch((e: Error) => setErr(e.message));
  }, [groupPath]);

  const cat = useMemo(() => findCat(CATEGORY_TREE, groupPath), [groupPath]);
  const node = useMemo(() => (tree ? findNode(tree, groupPath) : null), [tree, groupPath]);
  const accent = `var(--${cat?.cat ?? 'cat-world'})`;
  const icon = cat?.icon ?? 'world';
  const label = cat?.label ?? node?.label ?? groupPath;

  const facets = useMemo(() => new Map((pages ?? []).map((p) => [p.slug, facetsOf(p)])), [pages]);
  const types = useMemo(() => [...new Set((pages ?? []).map((p) => p.type).filter(Boolean))], [pages]);
  const tiers = useMemo(() => [...new Set((pages ?? []).map((p) => facets.get(p.slug)?.tier).filter(Boolean) as string[])].sort(), [pages, facets]);

  const shown = useMemo(() => (pages ?? []).filter((p) => {
    const f = facets.get(p.slug)!;
    if (q.trim() && !(p.titleUk + ' ' + p.titleEn).toLowerCase().includes(q.trim().toLowerCase())) return false;
    if (fType.size && !fType.has(p.type)) return false;
    if (fTier.size && !fTier.has(f.tier)) return false;
    return true;
  }).sort((a, b) => (a.titleUk || a.slug).localeCompare(b.titleUk || b.slug, 'uk')), [pages, facets, q, fType, fTier]);

  if (err) return <div className="wiki-error">⚠ {err}</div>;

  const toggle = (set: Set<string>, setSet: (s: Set<string>) => void, v: string): void => { const n = new Set(set); if (n.has(v)) n.delete(v); else n.add(v); setSet(n); };

  return (
    <div className="dv-dir">
      <div className="dv-dir__head">
        <span className="dv-dir__ico" style={{ background: `color-mix(in srgb, ${accent} 16%, transparent)`, color: accent }}><DvIcon name={icon} size={28} /></span>
        <div><div className="dv-dir__eyebrow">Каталог</div><h1 className="dv-dir__h1">{label}</h1></div>
      </div>

      {node && node.children.length > 0 && (
        <div className="dv-dir__subs">
          {node.children.map((c) => { const cc = findCat(CATEGORY_TREE, c.path); const ac = `var(--${cc?.cat ?? 'cat-world'})`; return (
            <Link key={c.path} to={`/wiki/_/${c.path}`} className="dv-dir__sub"><span style={{ color: ac }}><DvIcon name={cc?.icon ?? 'world'} size={16} /></span>{cc?.label ?? c.label}</Link>); })}
        </div>
      )}

      <div className="dv-dir__filters">
        <div className="dv-dir__search"><DvIcon name="search" size={15} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Пошук у каталозі…" /></div>
        {types.length > 1 && <div className="dv-dir__frow"><span className="dv-dir__flbl"><DvIcon name="filter" size={13} /> Тип</span>{types.map((t) => <button key={t} type="button" className={`dv-dir__fchip ${fType.has(t) ? 'on' : ''}`} onClick={() => toggle(fType, setFType, t)}>{getSchema(t)?.label ?? t}</button>)}</div>}
        {tiers.length > 0 && <div className="dv-dir__frow"><span className="dv-dir__flbl"><DvIcon name="filter" size={13} /> Рівень</span>{tiers.map((t) => <button key={t} type="button" className={`dv-dir__fchip ${fTier.has(t) ? 'on' : ''}`} onClick={() => toggle(fTier, setFTier, t)}>T{t}</button>)}</div>}
      </div>

      {!pages && <div className="wiki-loading">завантаження…</div>}
      {pages && <div className="dv-dir__count">Показано {shown.length} з {pages.length}</div>}

      <div className="dv-card-grid dv-dir__grid">
        {shown.map((p) => {
          const sc = getSchema(p.type); const ac = `var(--${sc?.cat ?? 'cat-world'})`; const f = facets.get(p.slug)!;
          const ep = sv(p.frontmatter as Record<string, unknown>, 'epithet');
          return (
            <Link key={p.slug} to={`/wiki/${encodeURIComponent(p.slug)}`} className="dv-dir__card">
              <span className="dv-dir__cardstripe" style={{ background: ac }} />
              <span className="dv-dir__cardico" style={{ background: `color-mix(in srgb, ${ac} 14%, transparent)`, color: ac }}><DvIcon name={sc?.icon ?? 'world'} size={22} /></span>
              <span className="dv-dir__cardbody">
                <span className="dv-dir__cardname">{p.titleUk || p.slug}</span>
                {ep && <span className="dv-dir__cardep">{ep}</span>}
              </span>
              <span className="dv-dir__cardchips">{f.tier && <span className={`dv-chip dv-chip--tier${f.tier}`}>T{f.tier}</span>}{f.status && <span className="dv-chip">{f.status}</span>}</span>
            </Link>
          );
        })}
      </div>
      {pages && shown.length === 0 && <div className="dv-dir__empty">{pages.length === 0 ? 'Ця категорія поки порожня. Створи першу сутність кнопкою «Створити».' : 'Нічого не знайдено за фільтрами.'}</div>}
    </div>
  );
}
